import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

/**
 * The CENTERLINE AI page's styles: CENTERLINE's theme (customTheme.tsx) as the CENTERLINE AI design uses
 * it. It sidesteps two global theme rules meant for other places: MuiListItemText's light-blue
 * text (the nav drawer's) and the uppercase `body2` variant.
 */

const NAVY = '#0A2F85';
const BLUE_50 = '#F3F7FF';
const BLUE_100 = '#E6EEFF';
const BLUE_400 = '#73A3FF';
const BLUE_500 = '#4380ED';
const GRAY_50 = '#F9F9F9';
const GRAY_100 = '#EDECEC';
const GRAY_200 = '#C9C8C9';
const GRAY_300 = '#B2B1B2';
const GRAY_400 = '#949494';
const GRAY_600 = '#616061';
const GRAY_700 = '#464546';
const RED_400 = '#D02C14';
const CARD_SHADOW = '0 5px 15px rgba(0, 0, 0, 0.15)';
const POPOVER_SHADOW = '0 8px 24px rgba(0, 0, 0, 0.18)';
export const MONO = "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const thinScrollbar = {
  scrollbarWidth: 'thin' as const,
  '&::-webkit-scrollbar': { width: 8, height: 8 },
  '&::-webkit-scrollbar-thumb': { background: GRAY_200, borderRadius: 4 },
};

const resetButton = {
  background: 'transparent',
  border: 'none',
  margin: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
};

const focusRing = (theme: Theme) => ({
  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
});

const sectionLabel = {
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: 0.8,
  textTransform: 'uppercase' as const,
  color: GRAY_600,
};

const popover = {
  position: 'absolute' as const,
  bottom: 'calc(100% + 8px)',
  left: 0,
  zIndex: 20,
  boxSizing: 'border-box' as const,
  background: '#fff',
  border: `1px solid ${GRAY_200}`,
  borderRadius: 4,
  boxShadow: POPOVER_SHADOW,
};

const useCenterlineAiStyles = makeStyles((theme: Theme) =>
  createStyles({
    '@keyframes cbRise': {
      from: { opacity: 0, transform: 'translateY(6px)' },
      to: { opacity: 1, transform: 'none' },
    },
    '@keyframes cbBlink': { '50%': { opacity: 0 } },
    '@keyframes cbSpin': { to: { transform: 'rotate(360deg)' } },

    /* ---- page ---- */
    page: {
      flexGrow: 1,
      minWidth: 0,
      height: '100vh',
      minHeight: 600,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      padding: theme.spacing(3),
      paddingTop: theme.spacing(1.5),
      background: GRAY_50,
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      color: GRAY_700,
      // CssBaseline gives <body> the theme's body2 (uppercase, medium weight); undo both here.
      textTransform: 'none',
      fontWeight: 400,
      // On a phone 100vh includes the browser's toolbars, which would cover the composer; dvh is
      // the height actually showing.
      '@supports (height: 100dvh)': { height: '100dvh' },
      [theme.breakpoints.down('xs')]: {
        minHeight: 480,
        gap: 12,
        padding: theme.spacing(1),
        paddingTop: theme.spacing(3),
      },
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '12px 24px',
    },
    titleBlock: { display: 'flex', flexDirection: 'column', gap: 8 },
    // Doubled to beat the app's global `.main h1` / `.main h2` rules.
    title: {
      '&&': {
        margin: 0,
        fontFamily: 'Roboto, sans-serif',
        fontSize: 34,
        lineHeight: '40px',
        fontWeight: 700,
        letterSpacing: '-0.6px',
        color: theme.palette.primary.main,
        textTransform: 'none',
        [theme.breakpoints.down('xs')]: { fontSize: 26, lineHeight: '32px' },
      },
    },
    subtitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 15,
      lineHeight: '22px',
      color: GRAY_700,
      // A phone needs the height for the conversation; the project box says what it scopes.
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    statusDot: { width: 8, height: 8, borderRadius: 4, background: '#2E7D32' },
    scope: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      paddingTop: 8,
      [theme.breakpoints.down('xs')]: { width: '100%', paddingTop: 0 },
    },
    projectSelect: {
      width: 380,
      [theme.breakpoints.down('xs')]: { width: '100%' },
      '& .MuiOutlinedInput-root': { background: '#fff', paddingTop: 0, paddingBottom: 0 },
      '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
        padding: '12px 4px 12px 6px',
        fontSize: 15,
        color: GRAY_700,
      },
      '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
        transform: 'translate(14px, 14px) scale(1)',
      },
      '& .MuiFormHelperText-root': {
        margin: '4px 0 0 14px',
        fontSize: 12,
        lineHeight: '16px',
        color: GRAY_400,
        fontFamily: 'Roboto, sans-serif',
        '@media (max-width: 900px)': { display: 'none' },
      },
    },
    projectOption: { fontSize: 14, color: GRAY_700, textTransform: 'none' },
    projectFailed: {
      height: 44,
      display: 'flex',
      alignItems: 'center',
      padding: '0 14px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      fontSize: 15,
      color: RED_400,
      width: 380,
      boxSizing: 'border-box',
    },

    /* ---- body: the chats card and the conversation card ---- */
    body: { flexGrow: 1, minHeight: 0, display: 'flex', gap: 16 },
    card: {
      background: '#fff',
      borderRadius: 4,
      boxShadow: CARD_SHADOW,
      overflow: 'hidden',
    },
    notice: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      padding: 24,
      textAlign: 'center',
      fontSize: 15,
      lineHeight: '22px',
    },

    /* ---- chats sidebar ---- */
    sidebar: {
      width: 288,
      flexShrink: 0,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.22s',
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    },
    sidebarRail: { width: 56 },
    // The narrow layout's chats panel (ChatsAndConversation): the sidebar filling the drawer. The
    // drawer is portalled outside the page, so it undoes CssBaseline's body2 itself (see `page`).
    chatsDrawerPaper: {
      width: 320,
      maxWidth: '86vw',
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      textTransform: 'none',
      color: GRAY_700,
    },
    sidebarPanel: {
      width: '100%',
      flexGrow: 1,
      minHeight: 0,
      borderRadius: 0,
      boxShadow: 'none',
      transition: 'none',
    },
    sidebarHead: {
      height: 52,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px 0 20px',
      background: NAVY,
      color: '#fff',
    },
    sidebarHeadRail: { justifyContent: 'center', padding: 0 },
    sidebarTitle: {
      '&&': {
        margin: 0,
        fontFamily: 'Roboto, sans-serif',
        fontSize: 20,
        lineHeight: '24px',
        fontWeight: 700,
        color: '#fff',
      },
    },
    headButton: {
      ...resetButton,
      width: 36,
      height: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      color: '#fff',
      '&:hover': { background: 'rgba(255, 255, 255, 0.12)' },
      '&:focus-visible': { outline: '2px solid #fff', outlineOffset: -2 },
    },
    headButtonRail: { transform: 'rotate(180deg)' },
    newChatWrap: { padding: '16px 16px 8px' },
    newChat: {
      ...resetButton,
      width: '100%',
      height: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      borderRadius: 4,
      background: theme.palette.primary.main,
      color: '#fff',
      fontSize: 14,
      fontWeight: 500,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      '&:hover': { background: BLUE_500 },
      ...focusRing(theme),
    },
    newChatRail: {
      ...resetButton,
      width: 36,
      height: 36,
      margin: '12px auto 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      background: theme.palette.primary.main,
      color: '#fff',
      '&:hover': { background: BLUE_500 },
      ...focusRing(theme),
    },
    chatList: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      listStyle: 'none',
      margin: 0,
      padding: '4px 8px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      ...thinScrollbar,
    },
    chatsEmpty: { fontSize: 14, color: GRAY_400, padding: '10px 12px' },
    chatRow: {
      position: 'relative',
      flexShrink: 0,
      height: 40,
      display: 'flex',
      alignItems: 'center',
      borderRadius: 4,
      '&:hover': { background: GRAY_50 },
      '&:hover $rowMore, &:focus-within $rowMore': { opacity: 1 },
    },
    chatRowActive: {
      '&, &:hover': { background: BLUE_100 },
      '& $rowOpen': { color: theme.palette.primary.main, fontWeight: 500 },
      '& $rowMore': { opacity: 1 },
    },
    rowOpen: {
      ...resetButton,
      flexGrow: 1,
      minWidth: 0,
      height: 40,
      padding: '0 8px 0 12px',
      textAlign: 'left',
      fontSize: 15,
      color: GRAY_700,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      borderRadius: 4,
      ...focusRing(theme),
    },
    rowMore: {
      ...resetButton,
      opacity: 0,
      width: 32,
      height: 32,
      flexShrink: 0,
      marginRight: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      color: GRAY_600,
      '&[aria-expanded="true"]': { opacity: 1 },
      // A touch screen has no hover to reveal it.
      '@media (hover: none)': { opacity: 1 },
      '&:hover': { background: 'rgba(9, 71, 185, 0.08)' },
      ...focusRing(theme),
    },
    renameInput: {
      flexGrow: 1,
      minWidth: 0,
      height: 32,
      boxSizing: 'border-box',
      margin: '0 4px',
      padding: '0 8px',
      fontSize: 15,
      fontFamily: 'Roboto, sans-serif',
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: 4,
      outline: 'none',
      color: GRAY_700,
      background: '#fff',
    },
    rowMenuPaper: { width: 168, borderRadius: 4, boxShadow: POPOVER_SHADOW },
    menuItem: {
      textTransform: 'none',
      gap: 10,
      padding: '9px 14px',
      fontSize: 14,
      fontFamily: 'Roboto, sans-serif',
      color: GRAY_700,
      minHeight: 0,
    },
    menuDanger: { color: RED_400 },
    sidebarFoot: {
      margin: 0,
      padding: '12px 20px',
      borderTop: `1px solid ${GRAY_100}`,
      fontSize: 12,
      lineHeight: '16px',
      color: GRAY_400,
    },

    /* ---- conversation ---- */
    conversation: {
      flexGrow: 1,
      minWidth: 0,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    },
    conversationWelcome: { justifyContent: 'center' },
    // Narrow, the bar stays at the top of an empty chat and the welcome and composer are centred
    // in the room below it: the free space goes to the two auto margins, not to justifyContent.
    conversationNarrow: {
      '&$conversationWelcome $conversationBar, &$conversationWelcome $composerBarWelcome': {
        marginBottom: 'auto',
      },
    },
    conversationBar: {
      height: 48,
      flexShrink: 0,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '0 6px',
      borderBottom: `1px solid ${GRAY_100}`,
    },
    barButton: {
      ...resetButton,
      height: 36,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '0 10px',
      borderRadius: 4,
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 500,
      '&:hover': { background: BLUE_50 },
      ...focusRing(theme),
    },
    barTitle: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 14,
      color: GRAY_600,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    barIconButton: {
      ...resetButton,
      width: 36,
      height: 36,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      color: theme.palette.primary.main,
      '&:hover': { background: BLUE_50 },
      ...focusRing(theme),
    },
    messages: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: '24px 16px 12px',
      display: 'flex',
      flexDirection: 'column',
      ...thinScrollbar,
      [theme.breakpoints.down('xs')]: { padding: '16px 10px 8px' },
    },
    // An empty chat: the welcome block and the composer sit together in the middle of the card, and the
    // composer drops to the bottom as soon as a question is sent (Thread, Composer and the layout
    // switch on showsWelcome). These come after the rules they override.
    messagesWelcome: { flexGrow: 0 },
    thread: {
      width: '100%',
      maxWidth: 760,
      margin: '0 auto',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    },
    welcome: {
      margin: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      padding: '24px 8px',
      textAlign: 'center',
    },
    // centerline-logo-rounded.png is square with its rounded corners drawn in (transparent), so nothing
    // here rounds or crops it. Rounding a bitmap in CSS clipped its bottom edge in Safari wherever the
    // centred layout put the image on a fractional pixel.
    welcomeLogo: {
      display: 'block',
      width: 48,
      height: 48,
    },
    welcomeIcon: {
      width: 56,
      height: 56,
      borderRadius: 8,
      background: BLUE_100,
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    welcomeTitle: {
      '&&': {
        margin: '4px 0 0',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 24,
        lineHeight: '30px',
        fontWeight: 700,
        color: theme.palette.primary.main,
      },
    },
    welcomeText: { margin: 0, fontSize: 15, lineHeight: '22px', color: GRAY_700 },

    row: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      animation: '$cbRise 0.18s ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    rowUser: {
      flexDirection: 'row-reverse',
      // UserAvatar, which has no class of its own; hidden on a phone like the other avatars.
      [theme.breakpoints.down('xs')]: { '& > svg': { display: 'none' } },
    },
    avatar: {
      width: 32,
      height: 32,
      flexShrink: 0,
      borderRadius: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      userSelect: 'none',
      // On a phone the bubbles need the width more; their side and colour say who is speaking.
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    // CENTERLINE AI's answers: the welcome screen's rounded CL logo file (see welcomeLogo), at avatar size.
    avatarLogo: {
      display: 'block',
      width: 32,
      height: 32,
      flexShrink: 0,
      userSelect: 'none',
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    avatarError: { background: RED_400, fontSize: 17, fontWeight: 700 },
    userBubble: {
      maxWidth: 560,
      minWidth: 0,
      padding: '10px 14px',
      borderRadius: '8px 8px 2px 8px',
      background: theme.palette.primary.main,
      color: '#fff',
      fontSize: 15,
      lineHeight: '22px',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
    },
    instructions: {
      marginTop: 8,
      fontSize: 12,
      '& summary': { cursor: 'pointer', color: GRAY_600 },
      '& pre': {
        margin: '6px 0 0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: MONO,
        fontSize: 11,
        lineHeight: '16px',
        maxHeight: 240,
        overflowY: 'auto',
      },
    },
    botBubble: {
      flex: '1 1 auto',
      minWidth: 0,
      maxWidth: 690,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      padding: '12px 16px',
      border: `1px solid ${GRAY_100}`,
      borderRadius: '2px 8px 8px 8px',
      background: GRAY_50,
      [theme.breakpoints.down('xs')]: { padding: '10px 12px' },
    },
    errorBubble: {
      maxWidth: 690,
      minWidth: 0,
      boxSizing: 'border-box',
      padding: '10px 14px',
      border: '1px solid #F3C2BA',
      borderRadius: '2px 8px 8px 8px',
      background: '#FDECEA',
      fontSize: 15,
      lineHeight: '22px',
      color: '#A3200F',
      whiteSpace: 'pre-wrap',
    },
    msgTags: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: 6,
      margin: '-6px 42px 0 0',
      [theme.breakpoints.down('xs')]: { marginRight: 0 },
    },
    tagPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      maxWidth: 260,
      padding: '0 8px',
      borderRadius: 10,
      background: BLUE_100,
      color: theme.palette.primary.main,
      fontSize: 12,
      lineHeight: '20px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    tagMono: { fontFamily: MONO },

    /* ---- trace ---- */
    traceSummary: {
      ...resetButton,
      alignSelf: 'flex-start',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      textAlign: 'left',
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.primary.main,
      borderRadius: 2,
      '& svg': { transition: 'transform .15s' },
      ...focusRing(theme),
    },
    traceSummaryOpen: { '& svg': { transform: 'rotate(90deg)' } },
    traceSteps: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    traceStep: { display: 'flex', flexDirection: 'column', gap: 6 },
    traceStepParallel: { paddingLeft: 10, borderLeft: `2px solid ${BLUE_400}` },
    traceStepBatchStart: { marginTop: 6 },
    traceHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    spinner: {
      animation: '$cbSpin 0.9s linear infinite',
      '@media (prefers-reduced-motion: reduce)': { animationDuration: '2.4s' },
    },
    traceTool: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '2px 8px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      fontFamily: MONO,
      fontSize: 12,
      color: GRAY_700,
    },
    traceToolRunning: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    traceToolError: { borderColor: RED_400, color: RED_400 },
    traceFlow: { fontSize: 12, color: GRAY_400 },
    traceLabel: { fontSize: 14, lineHeight: '20px', color: GRAY_600, overflowWrap: 'anywhere' },
    traceLabelRunning: { fontWeight: 500, color: theme.palette.primary.main },
    traceLabelError: { color: RED_400 },
    traceCode: {
      margin: 0,
      padding: '8px 10px',
      border: `1px solid ${GRAY_100}`,
      borderRadius: 4,
      background: '#fff',
      fontFamily: MONO,
      fontSize: 12,
      lineHeight: '18px',
      color: GRAY_700,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      maxHeight: 240,
      overflowY: 'auto',
      ...thinScrollbar,
    },
    traceCodeError: { opacity: 0.6 },
    traceShot: {
      display: 'inline-block',
      alignSelf: 'flex-start',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      overflow: 'hidden',
      lineHeight: 0,
      '&:hover': { borderColor: theme.palette.primary.main },
      '& img': { maxWidth: 'min(460px, 100%)', display: 'block' },
    },
    thinking: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      fontSize: 14,
      lineHeight: '20px',
      fontStyle: 'italic',
      color: GRAY_400,
      whiteSpace: 'pre-wrap',
      '& svg': { marginTop: 2 },
    },

    /* ---- answer ---- */
    answer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      fontSize: 15,
      lineHeight: '22px',
      color: GRAY_700,
      overflowWrap: 'anywhere',
      '&& p, && h1, && h2, && h3, && ul, && ol, && pre': { margin: 0 },
      '&& h1, && h2, && h3': {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 16,
        lineHeight: '22px',
        fontWeight: 700,
        color: GRAY_700,
      },
      '& ul, & ol': { paddingLeft: 22 },
      '& li + li': { marginTop: 2 },
      '& code': {
        fontFamily: MONO,
        fontSize: '0.86em',
        padding: '1px 4px',
        borderRadius: 3,
        background: BLUE_100,
      },
      '& pre': {
        padding: '8px 10px',
        border: `1px solid ${GRAY_100}`,
        borderRadius: 4,
        background: '#fff',
        overflowX: 'auto',
        fontSize: 12,
        lineHeight: '18px',
        whiteSpace: 'pre',
        ...thinScrollbar,
      },
      '& pre code': { padding: 0, background: 'none', fontSize: 'inherit' },
      '& a': { color: theme.palette.primary.main },
      '& a:hover': { color: BLUE_500 },
    },
    answerPending: { fontStyle: 'italic', color: GRAY_400 },
    answerError: { color: RED_400 },
    answerStreaming: {
      '& > :last-child::after': {
        content: '"▍"',
        marginLeft: 1,
        fontWeight: 700,
        color: theme.palette.primary.main,
        animation: '$cbBlink 1s steps(1) infinite',
      },
    },
    tableWrap: {
      maxWidth: '100%',
      maxHeight: 420,
      overflow: 'auto',
      border: `1px solid ${GRAY_100}`,
      borderRadius: 4,
      background: '#fff',
      ...thinScrollbar,
      ...focusRing(theme),
      '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 14,
        lineHeight: '20px',
        color: GRAY_600,
      },
      '& th': {
        position: 'sticky',
        top: 0,
        padding: '8px 12px',
        background: '#fff',
        borderBottom: `1px solid ${GRAY_100}`,
        textAlign: 'left',
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: GRAY_400,
        whiteSpace: 'nowrap',
      },
      '& td': { padding: '6px 12px', whiteSpace: 'nowrap' },
      '& tbody tr:nth-of-type(even)': { background: GRAY_50 },
    },
    cite: {
      fontWeight: 700,
      textDecoration: 'none',
      '&&': { color: theme.palette.primary.main },
      '&:hover': { textDecoration: 'underline' },
      ...focusRing(theme),
    },
    citeInfo: {
      ...resetButton,
      padding: '0 2px',
      borderRadius: 3,
      fontWeight: 700,
      color: theme.palette.primary.main,
      '&:hover, &[aria-expanded="true"]': { background: BLUE_100 },
      ...focusRing(theme),
    },
    citeCard: { zIndex: 1300 },
    citeCardBody: {
      width: 360,
      maxWidth: 'calc(100vw - 16px)',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      padding: '14px 16px',
      background: '#fff',
      borderRadius: 4,
      boxShadow: POPOVER_SHADOW,
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      color: GRAY_700,
      textTransform: 'none',
    },
    citeCardHead: sectionLabel,
    citeFields: {
      margin: 0,
      display: 'grid',
      gridTemplateColumns: '96px minmax(0, 1fr)',
      gap: '6px 12px',
      fontSize: 13,
      lineHeight: '20px',
      '& dt': { color: GRAY_400 },
      '& dd': { margin: 0, color: GRAY_700, overflowWrap: 'anywhere' },
    },
    idButton: {
      ...resetButton,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '1px 6px',
      border: `1px solid ${GRAY_100}`,
      borderRadius: 4,
      background: GRAY_50,
      fontFamily: MONO,
      fontSize: 11,
      color: GRAY_600,
      '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
      ...focusRing(theme),
    },
    citeNote: {
      margin: 0,
      fontSize: 12,
      lineHeight: '16px',
      fontStyle: 'italic',
      color: GRAY_400,
    },

    usage: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px 14px',
      paddingTop: 8,
      borderTop: `1px solid ${GRAY_100}`,
      fontSize: 12,
      lineHeight: '18px',
      color: GRAY_400,
      fontVariantNumeric: 'tabular-nums',
      '& b': { fontWeight: 500, color: GRAY_600 },
    },

    feedback: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      paddingTop: 8,
      borderTop: `1px solid ${GRAY_100}`,
    },
    feedbackButtons: { display: 'flex', gap: 6 },
    feedbackButton: {
      ...resetButton,
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      color: GRAY_400,
      '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
      '&[aria-pressed="true"]': {
        borderColor: theme.palette.primary.main,
        background: BLUE_100,
        color: theme.palette.primary.main,
      },
      ...focusRing(theme),
    },
    reasons: { display: 'flex', flexWrap: 'wrap', gap: 6 },
    reason: {
      ...resetButton,
      height: 28,
      padding: '0 12px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 14,
      background: '#fff',
      fontSize: 13,
      color: GRAY_700,
      '&:hover': { borderColor: theme.palette.primary.main },
      '&[aria-pressed="true"]': {
        borderColor: theme.palette.primary.main,
        background: theme.palette.primary.main,
        color: '#fff',
      },
      ...focusRing(theme),
    },
    note: {
      width: 320,
      maxWidth: '100%',
      height: 32,
      boxSizing: 'border-box',
      padding: '0 10px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      fontSize: 13,
      fontFamily: 'Roboto, sans-serif',
      color: GRAY_700,
      outline: 'none',
      '&:focus': { borderColor: theme.palette.primary.main },
    },

    /* ---- composer ---- */
    composerBar: {
      display: 'flex',
      justifyContent: 'center',
      padding: '12px 16px',
      borderTop: `1px solid ${GRAY_100}`,
      [theme.breakpoints.down('xs')]: { padding: '8px 10px' },
    },
    composerBarWelcome: { borderTop: 'none' },
    composer: {
      position: 'relative',
      width: '100%',
      maxWidth: 760,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      margin: 0,
    },
    composerRow: { display: 'flex', alignItems: 'flex-end', gap: 8 },
    field: {
      flexGrow: 1,
      minWidth: 0,
      minHeight: 52,
      boxSizing: 'border-box',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 6,
      padding: '6px 8px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      '&:focus-within': {
        padding: '5px 7px',
        border: `2px solid ${theme.palette.primary.main}`,
      },
      // The same height as the send button: its 32px buttons plus this padding and border make 44.
      [theme.breakpoints.down('xs')]: {
        minHeight: 44,
        padding: '4px 8px',
        '&:focus-within': { padding: '3px 7px' },
      },
    },
    fieldBusy: { background: GRAY_50 },
    toolButton: {
      ...resetButton,
      width: 32,
      height: 32,
      flexShrink: 0,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      color: theme.palette.primary.main,
      '& svg': { transition: 'transform .15s' },
      '&:hover': { borderColor: theme.palette.primary.main },
      ...focusRing(theme),
    },
    toolButtonOpen: {
      borderColor: theme.palette.primary.main,
      background: BLUE_100,
      '& svg': { transform: 'rotate(45deg)' },
    },
    micButton: {
      ...resetButton,
      width: 32,
      height: 32,
      flexShrink: 0,
      marginLeft: 'auto',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      color: theme.palette.primary.main,
      '&:hover:not(:disabled)': { borderColor: theme.palette.primary.main },
      '&:disabled': { color: GRAY_300, cursor: 'not-allowed' },
      ...focusRing(theme),
    },
    micButtonListening: {
      borderColor: theme.palette.primary.main,
      background: theme.palette.primary.main,
      color: '#fff',
      animation: '$cbMicPulse 1.6s ease-in-out infinite',
      '&:hover:not(:disabled)': { background: BLUE_500, borderColor: BLUE_500 },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes cbMicPulse': {
      '0%, 100%': { boxShadow: `0 0 0 0 ${BLUE_400}` },
      '50%': { boxShadow: `0 0 0 5px ${BLUE_100}` },
    },
    dictationHint: {
      margin: 0,
      minHeight: 0,
      fontSize: 13,
      lineHeight: '18px',
      color: GRAY_600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      // An empty hint must not add a gap to the form's column.
      '&:empty': { marginTop: -8 },
    },
    dictationInterim: { fontStyle: 'italic', color: GRAY_700 },
    textarea: {
      flex: '1 1 240px',
      minWidth: 160,
      height: 30,
      maxHeight: 160,
      boxSizing: 'border-box',
      padding: 4,
      border: 'none',
      outline: 'none',
      resize: 'none',
      background: 'transparent',
      fontFamily: 'Roboto, sans-serif',
      fontSize: 15,
      lineHeight: '22px',
      color: GRAY_700,
      '&::placeholder': { color: GRAY_300, opacity: 1 },
      '&:disabled': { color: GRAY_300 },
      // Narrow enough to share a phone's line with the "+" button; the placeholder stays on one
      // line so the field keeps the send button's height.
      [theme.breakpoints.down('xs')]: {
        flexBasis: 120,
        minWidth: 100,
        '&::placeholder': { whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
      },
    },
    sendButton: {
      ...resetButton,
      width: 52,
      height: 52,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      background: theme.palette.primary.main,
      color: '#fff',
      '&:hover:not(:disabled)': { background: BLUE_500 },
      '&:disabled': { background: '#DCDCDC', cursor: 'not-allowed' },
      ...focusRing(theme),
      [theme.breakpoints.down('xs')]: { width: 44, height: 44 },
    },
    stopButton: { background: RED_400, '&:hover:not(:disabled)': { background: '#A3200F' } },
    disclaimer: {
      margin: 0,
      textAlign: 'center',
      fontSize: 12,
      lineHeight: '16px',
      color: GRAY_400,
    },

    chip: {
      height: 26,
      maxWidth: 230,
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2,
      padding: '0 3px 0 10px',
      borderRadius: 13,
      background: BLUE_100,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.primary.main,
    },
    chipType: { background: NAVY, color: '#fff' },
    chipTool: { fontFamily: MONO, fontSize: 12, gap: 6, paddingLeft: 8 },
    chipLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    chipX: {
      ...resetButton,
      width: 20,
      height: 20,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 10,
      '&:hover': { background: 'rgba(9, 71, 185, 0.14)' },
      ...focusRing(theme),
    },
    chipXOnNavy: { '&:hover': { background: 'rgba(255, 255, 255, 0.2)' } },
    contextChips: { display: 'contents' },
    clearChips: {
      ...resetButton,
      padding: '0 4px',
      fontSize: 13,
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      ...focusRing(theme),
    },

    /* ---- "+" tools menu ---- */
    toolMenu: {
      ...popover,
      width: 430,
      maxWidth: '100%',
      // --cl-ai-popover-room is set by the composer while an empty chat is centred (Composer.tsx): the
      // menu opens upward, so it must fit between the card's top edge and the composer.
      maxHeight: 'min(66vh, 560px, var(--cl-ai-popover-room, 560px))',
      overflowY: 'auto',
      padding: '6px 0',
      ...thinScrollbar,
    },
    menuHead: { ...sectionLabel, padding: '6px 14px' },
    toolItem: {
      ...resetButton,
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '6px 14px',
      textAlign: 'left',
      outline: 'none',
      '& > svg': { marginTop: 2, color: theme.palette.primary.main },
      '&:hover, &:focus': { background: BLUE_50 },
      '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` },
    },
    toolItemDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      '& $toolDesc': { fontStyle: 'italic' },
      '&:hover': { background: 'transparent' },
    },
    toolText: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
    toolName: {
      display: 'flex',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: '0 8px',
      fontSize: 14,
      fontWeight: 500,
      color: GRAY_700,
    },
    toolId: { fontFamily: MONO, fontSize: 11, fontWeight: 400, color: GRAY_400 },
    toolDesc: { fontSize: 13, lineHeight: '18px', color: GRAY_600 },
    rosterToggle: {
      ...resetButton,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      margin: '0 0 4px 44px',
      padding: '2px 0',
      fontSize: 12,
      color: theme.palette.primary.main,
      '& svg': { transition: 'transform .15s' },
      '&[aria-expanded="true"] svg': { transform: 'rotate(90deg)' },
      ...focusRing(theme),
    },
    roster: {
      listStyle: 'none',
      margin: '0 14px 6px 44px',
      padding: '0 0 0 10px',
      borderLeft: `2px solid ${GRAY_100}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    rosterOff: { opacity: 0.5 },
    rosterRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      '& > svg': { marginTop: 3, color: GRAY_600 },
      '& $toolName': { fontSize: 13 },
      '& $toolDesc': { fontSize: 12, lineHeight: '16px' },
    },

    /* ---- "@" picker ---- */
    picker: {
      ...popover,
      right: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    pickerHead: {
      display: 'flex',
      gap: 8,
      padding: 10,
      borderBottom: `1px solid ${GRAY_100}`,
    },
    pickerSearch: {
      flexGrow: 1,
      minWidth: 0,
      height: 36,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '0 10px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: '#fff',
      color: GRAY_400,
      '&:focus-within': { padding: '0 9px', border: `2px solid ${theme.palette.primary.main}` },
      '& input': {
        flexGrow: 1,
        minWidth: 0,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: 14,
        fontFamily: 'Roboto, sans-serif',
        color: GRAY_700,
      },
    },
    pickerSelect: {
      width: 240,
      maxWidth: '44%',
      height: 36,
      padding: '0 10px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 4,
      background: GRAY_50,
      fontSize: 14,
      fontFamily: 'Roboto, sans-serif',
      color: GRAY_700,
      '&:disabled': { color: GRAY_300 },
    },
    pickerList: {
      // The picker's search row and footer take about 130px of the room.
      maxHeight: 'max(120px, min(46vh, 340px, calc(var(--cl-ai-popover-room, 9999px) - 130px)))',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      padding: '4px 0',
      ...thinScrollbar,
    },
    pickerSection: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 8,
      padding: '8px 14px 4px',
      '& > span:first-child': sectionLabel,
    },
    pickerSectionNext: { marginTop: 4, paddingTop: 10, borderTop: `1px solid ${GRAY_100}` },
    sectionNote: { fontSize: 12, fontStyle: 'italic', color: GRAY_400 },
    pickerRow: {
      minHeight: 34,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '0 14px',
      cursor: 'pointer',
      outline: 'none',
      '&:hover, &:focus': { background: BLUE_50 },
      '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` },
    },
    pickerRowOn: { background: BLUE_50 },
    tick: {
      width: 18,
      height: 18,
      flexShrink: 0,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1.5px solid ${GRAY_400}`,
      borderRadius: 3,
      background: '#fff',
      color: '#fff',
    },
    tickOn: { border: 'none', background: theme.palette.primary.main },
    rowName: {
      flexGrow: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 14,
      color: GRAY_700,
    },
    rowNameStrong: { fontWeight: 500 },
    rowMeta: { fontSize: 12, color: GRAY_400, fontVariantNumeric: 'tabular-nums' },
    extBadge: {
      width: 40,
      flexShrink: 0,
      textAlign: 'center',
      borderRadius: 3,
      fontSize: 11,
      lineHeight: '20px',
      fontWeight: 700,
      letterSpacing: 0.3,
      color: '#fff',
    },
    infoTag: {
      flexShrink: 0,
      maxWidth: 160,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: '0 8px',
      borderRadius: 10,
      background: BLUE_100,
      fontSize: 11,
      lineHeight: '20px',
      color: theme.palette.primary.main,
    },
    infoTagPlain: { background: GRAY_100, color: GRAY_600 },
    pickerMessage: { padding: '8px 14px', fontSize: 13, color: GRAY_400 },
    pickerFoot: {
      padding: '8px 14px',
      borderTop: `1px solid ${GRAY_100}`,
      fontSize: 12,
      lineHeight: '16px',
      color: GRAY_600,
    },

    /* ---- run options (hidden unless the build shows them) ---- */
    runBar: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
    runPill: {
      position: 'relative',
      height: 32,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: '0 0 0 12px',
      border: `1px solid ${GRAY_200}`,
      borderRadius: 16,
      background: '#fff',
      '& > span': { ...sectionLabel, fontSize: 11 },
      '& select': {
        height: 30,
        padding: '0 12px 0 4px',
        border: 'none',
        borderRadius: 16,
        background: 'transparent',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'Roboto, sans-serif',
        color: theme.palette.primary.main,
      },
    },
    runHint: {
      marginLeft: 'auto',
      fontSize: 12,
      lineHeight: '16px',
      color: GRAY_400,
      '@media (max-width: 620px)': { display: 'none' },
    },
  }),
);

export default useCenterlineAiStyles;
